import { useState, useEffect } from 'react';
import { translations } from '../../translations';
import './Gallery.css';

const Gallery = ({ language = 'en' }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const t = translations[language]?.gallery || translations.en.gallery;

  const S3_BASE = 'https://instacarspa.s3.us-east-1.amazonaws.com';
  const images = [
    { src: `${S3_BASE}/1.png`, category: 'exterior', label: t.labels.tireCleaning },
    { src: `${S3_BASE}/2.png`, category: 'exterior', label: t.labels.ceramicCoating },
    { src: `${S3_BASE}/3.png`, category: 'exterior', label: t.labels.handWash },
    { src: `${S3_BASE}/4.png`, category: 'exterior', label: t.labels.deepCleaning },
    { src: `${S3_BASE}/5.png`, category: 'interior', label: t.labels.detailWork },
    { src: `${S3_BASE}/6.png`, category: 'interior', label: t.labels.vacuumService },
    { src: `${S3_BASE}/7.png`, category: 'interior', label: t.labels.brushCleaning },
    { src: `${S3_BASE}/8.png`, category: 'interior', label: t.labels.shammyFinish },
  ];



  const filteredImages = activeFilter === 'all' 
    ? images 
    : images.filter(img => img.category === activeFilter);

  const testimonials = [
    ...t.testimonials.map(item => ({ ...item, rating: 5 })),
    ...t.testimonials.map(item => ({ ...item, rating: 5 })),
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % Math.ceil(testimonials.length / 2));
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const getVisibleTestimonials = () => {
    const startIndex = currentTestimonial * 2;
    return testimonials.slice(startIndex, startIndex + 2);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxImage(filteredImages[index].src);
  };

  const navigateLightbox = (direction) => {
    const newIndex = direction === 'next' 
      ? (lightboxIndex + 1) % filteredImages.length
      : (lightboxIndex - 1 + filteredImages.length) % filteredImages.length;
    setLightboxIndex(newIndex);
    setLightboxImage(filteredImages[newIndex].src);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <svg key={i} className={`gallery__star ${i < rating ? 'gallery__star--filled' : ''}`} viewBox="0 0 576 512">
        <path d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z"/>
      </svg>
    ));
  };

  return (
    <section className="gallery" id="g">
      <div className="gallery__container">
        <div className="gallery__header">
          <span className="gallery__subtitle">{t.subtitle}</span>
          <h2 className="gallery__heading">{t.heading}</h2>
          <div className="gallery__divider">
            <span></span>
            <svg viewBox="0 0 512 512">
              <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 448c-110.5 0-200-89.5-200-200S145.5 56 256 56s200 89.5 200 200-89.5 200-200 200zm61.8-104.4l-84.9-61.7c-3.1-2.3-4.9-5.9-4.9-9.7V116c0-6.6 5.4-12 12-12h32c6.6 0 12 5.4 12 12v141.7l66.8 48.6c5.4 3.9 6.5 11.4 2.6 16.8L334.6 349c-3.9 5.3-11.4 6.5-16.8 2.6z"/>
            </svg>
            <span></span>
          </div>
        </div>

        <div className="gallery__filters">
          {[
            { key: 'all', label: t.filters.all },
            { key: 'exterior', label: t.filters.exterior },
            { key: 'interior', label: t.filters.interior },
          ].map((filter) => (
            <button
              key={filter.key}
              className={`gallery__filter ${activeFilter === filter.key ? 'gallery__filter--active' : ''}`}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
              {activeFilter === filter.key && <span className="gallery__filter-indicator"></span>}
            </button>
          ))}
        </div>

        <div className="gallery__grid">
          {filteredImages.map((image, index) => (
            <div
              key={index}
              className="gallery__item"
              onClick={() => openLightbox(index)}
            >
              <img src={image.src} alt={image.label} loading="lazy" />
              <div className="gallery__overlay">
                <div className="gallery__overlay-content">
                  <span className="gallery__overlay-icon">
                    <svg viewBox="0 0 512 512">
                      <path d="M508.5 468.9L387.1 347.5c-2.3-2.3-5.3-3.5-8.5-3.5h-13.2c31.5-36.5 50.6-84 50.6-136C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c52 0 99.5-19.1 136-50.6v13.2c0 3.2 1.3 6.2 3.5 8.5l121.4 121.4c4.7 4.7 12.3 4.7 17 0l22.6-22.6c4.7-4.7 4.7-12.3 0-17zM208 368c-88.4 0-160-71.6-160-160S119.6 48 208 48s160 71.6 160 160-71.6 160-160 160z"/>
                    </svg>
                  </span>
                  <span className="gallery__overlay-label">{image.label}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonials Section */}
        <div className="gallery__testimonials">
          <div className="gallery__testimonials-header">
            <h3 className="gallery__testimonials-title">{t.testimonialsTitle}</h3>
          </div>
          <div className="gallery__testimonials-slider">
            <div className="gallery__testimonials-track">
              {getVisibleTestimonials().map((testimonial, index) => (
                <div key={index} className="gallery__testimonial-card">
                  <div className="gallery__testimonial-quote">
                    <svg viewBox="0 0 512 512">
                      <path d="M464 256h-80v-64c0-35.3 28.7-64 64-64h8c13.3 0 24-10.7 24-24V56c0-13.3-10.7-24-24-24h-8c-88.4 0-160 71.6-160 160v240c0 26.5 21.5 48 48 48h128c26.5 0 48-21.5 48-48V304c0-26.5-21.5-48-48-48zm-288 0H96v-64c0-35.3 28.7-64 64-64h8c13.3 0 24-10.7 24-24V56c0-13.3-10.7-24-24-24h-8C71.6 32 0 103.6 0 192v240c0 26.5 21.5 48 48 48h128c26.5 0 48-21.5 48-48V304c0-26.5-21.5-48-48-48z"/>
                    </svg>
                  </div>
                  <p className="gallery__testimonial-text">{testimonial.text}</p>
                  <div className="gallery__testimonial-footer">
                    <div className="gallery__testimonial-rating">
                      {renderStars(testimonial.rating)}
                    </div>
                    <cite className="gallery__testimonial-name">{testimonial.name}</cite>
                  </div>
                </div>
              ))}
            </div>
            <div className="gallery__pagination">
              {Array.from({ length: Math.ceil(testimonials.length / 2) }).map((_, index) => (
                <button
                  key={index}
                  className={`gallery__pagination-dot ${index === currentTestimonial ? 'gallery__pagination-dot--active' : ''}`}
                  onClick={() => setCurrentTestimonial(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Lightbox */}
      {lightboxImage && (
        <div className="gallery__lightbox" onClick={() => setLightboxImage(null)}>
          <button className="gallery__lightbox-close" aria-label="Close">
            <svg viewBox="0 0 352 512">
              <path d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"/>
            </svg>
          </button>
          <button 
            className="gallery__lightbox-nav gallery__lightbox-nav--prev" 
            onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
            aria-label="Previous"
          >
            <svg viewBox="0 0 320 512">
              <path d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
            </svg>
          </button>
          <img src={lightboxImage} alt="Gallery" onClick={(e) => e.stopPropagation()} />
          <button 
            className="gallery__lightbox-nav gallery__lightbox-nav--next" 
            onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
            aria-label="Next"
          >
            <svg viewBox="0 0 320 512">
              <path d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/>
            </svg>
          </button>
          <div className="gallery__lightbox-counter">
            {lightboxIndex + 1} / {filteredImages.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
